import type { CSSProperties, JSX } from 'react'

import {
  Avatar,
  EllipsisVerticalIcon,
  Flex,
  SearchIcon,
} from '@buffer-mono/popcorn'

import { parseFacebookLinks } from '~publish/legacy/utils/channels/facebook/helpers'
import { parseTextToElements } from '~publish/legacy/utils/parseTextToElements'

import {
  CommentIcon,
  GlobeIcon,
  LikeIcon,
  ShareIcon,
} from '../../../../assets/facebook'
import { EmptyMediaPlaceholder, PostWrapper } from '../../../Common/styles'
import {
  DEFAULT_AVATAR,
  textExceedsCharLimit,
  truncateText,
} from '../../../Common/utils'
import { VideoPlayer } from '../../../Common/VideoPlayer'
import type { InstagramContent, ProfilePreview } from '../../../types'

import styles from './Reel.module.css'

const MAX_LENGTH = 30

const getText = (text: string): '' | ReturnType<typeof parseTextToElements> => {
  const truncatedText = truncateText(text, MAX_LENGTH, true)

  if (truncatedText) {
    return parseTextToElements(
      truncatedText,
      parseFacebookLinks(truncatedText),
      (element, key) => (
        <span
          key={key}
          data-testid="tiktok-preview-hashtag"
          className={styles.boldText}
        >
          {element.displayString}
        </span>
      ),
    )
  }

  return ''
}

const Text = ({ text }: { text: string }): JSX.Element => {
  const exceedsCharLimit = textExceedsCharLimit(text, MAX_LENGTH)
  const textToDisplay = getText(text)

  return textToDisplay ? (
    <div className={styles.caption}>
      <span translate="no">{textToDisplay}</span>
      {exceedsCharLimit && <span className={styles.more}>more</span>}
    </div>
  ) : (
    <></>
  )
}

const Reel = ({
  content,
  profile,
}: {
  content: InstagramContent
  profile: ProfilePreview
}): JSX.Element => {
  const { avatar, username } = profile
  const { text, video } = content

  const avatarStyle: CSSProperties & {
    '--avatar-src': string
    '--avatar-fallback-src': string
  } = {
    '--avatar-src': `url(${avatar})`,
    '--avatar-fallback-src': `url(${DEFAULT_AVATAR})`,
  }

  return (
    <PostWrapper data-testid="reels" className={styles.postWrapper}>
      <div className={styles.reelWrapper} data-testid="reel-wrapper">
        {video && <VideoPlayer video={video} className={styles.videoPlayer} />}
        {!video && (
          <Flex className={styles.emptyWrapper}>
            <EmptyMediaPlaceholder
              service="facebook"
              className={styles.empty}
            />
          </Flex>
        )}
        <div className={styles.metadataWrapper}>
          <div className={styles.user}>
            <Avatar
              data-testid="avatar"
              src={avatar}
              alt={username}
              size="small"
              fallback="image"
            />
            <div className={styles.usernameWrapper}>
              <span className={styles.username} translate="no">
                {username}
              </span>
              <GlobeIcon size="xsmall" color="white" />
            </div>
          </div>
          <Text text={text || ''} />
        </div>
        <div className={styles.iconsSideSection}>
          <LikeIcon size="medium" color="white" />
          <CommentIcon size="medium" color="white" />
          <ShareIcon size="medium" color="white" />
          <EllipsisVerticalIcon size={20} className={styles.icon} />
        </div>
      </div>

      <div className={styles.header}>
        <SearchIcon size={20} className={styles.search} />
        <div
          data-testid="avatar-square"
          className={styles.avatar}
          style={avatarStyle}
        />
      </div>
      <div className={styles.searchBox}>Add comment...</div>
    </PostWrapper>
  )
}

export default Reel
